import store from '@/store'
import Helper from '@/libs/Helper'
import useJwt from '@/auth/jwt/useJwt'

import subscriberBus, {
    NOTIFICATION_BUS_EVENTS,
} from '@/libs/subscriberBus'

import {
    HubConnectionBuilder,
    HubConnectionState,
    HttpTransportType,
    LogLevel,
} from '@microsoft/signalr'

const hubUrl = `${String(Helper.socketBaseUrl)
    .replace(/\/+$/, '')}/hub/NotifyHub`

const WITHDRAW_APPROVED_EVENT =
    NOTIFICATION_BUS_EVENTS.WITHDRAW_APPROVED_TO_BOOSTER
    || 'notify-hub:withdraw-approved-to-booster'

const activityHub = new HubConnectionBuilder()
    .withUrl(hubUrl, {
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true,
        accessTokenFactory() {
            return getAccessToken()
        },
    })
    .withAutomaticReconnect([
        0,
        2000,
        5000,
        10000,
        30000,
    ])
    .configureLogging(LogLevel.Warning)
    .build()

let startPromise = null
let restartTimer = null
let shouldBeConnected = false
let lastUserData = null

function getAccessToken() {
    const token = useJwt.getToken()

    if (!token) {
        return ''
    }

    return String(token)
        .replace(/^Bearer\s+/i, '')
        .replace(/^"(.*)"$/, '$1')
        .trim()
}

function emit(eventName, payload) {
    if (!eventName) {
        return
    }

    subscriberBus.$emit(eventName, payload)
}

function emitRefresh(source, payload = null) {
    emit(
        NOTIFICATION_BUS_EVENTS.REFRESH_NOTIFICATIONS,
        {
            source,
            payload,
        },
    )
}

function getArguments(argsLike) {
    return Array.prototype.slice.call(argsLike)
}

function normalizeArguments(args) {
    if (!Array.isArray(args) || args.length === 0) {
        return null
    }

    if (args.length === 1) {
        return args[0]
    }

    return args
}

function normalizeOrderEvent(args) {
    if (args.length === 1) {
        return args[0]
    }

    return {
        status: args[0],
        userId: args[1],
        code: args[2],
        message: args[3] || null,
    }
}

function normalizeBalanceEvent(args) {
    if (args.length === 1) {
        return args[0]
    }

    return {
        status: args[0],
        userId: args[1],
        message: args[2] || null,
    }
}

function commitNewNotification(payload) {
    if (
        store._mutations
        && store._mutations.setNewNotification
    ) {
        store.commit(
            'setNewNotification',
            payload,
        )
    }
}

function scheduleRestart() {
    if (
        restartTimer
        || !shouldBeConnected
    ) {
        return
    }

    restartTimer = setTimeout(
        async () => {
            restartTimer = null
            await startNotificationHub()
        },
        3000,
    )
}

activityHub.on(
    'UserNotifications',
    function userNotificationsListener() {
        const payload = normalizeArguments(
            getArguments(arguments),
        )

        console.log(
            '[NotifyHub] UserNotifications:',
            payload,
        )

        commitNewNotification(payload)

        emit(
            NOTIFICATION_BUS_EVENTS.USER_NOTIFICATION,
            payload,
        )
    },
)

activityHub.on(
    'UserData',
    function userDataListener() {
        const payload = normalizeArguments(
            getArguments(arguments),
        )

        lastUserData = payload

        console.log(
            '[NotifyHub] UserData:',
            payload,
        )

        emit(
            NOTIFICATION_BUS_EVENTS.USER_DATA,
            payload,
        )
    },
)

activityHub.on(
    'OrderEvent',
    function orderEventListener() {
        const payload = normalizeOrderEvent(
            getArguments(arguments),
        )

        console.log(
            '[NotifyHub] OrderEvent:',
            payload,
        )

        emit(
            NOTIFICATION_BUS_EVENTS.ORDER_EVENT,
            payload,
        )

        emitRefresh(
            'OrderEvent',
            payload,
        )
    },
)

activityHub.on(
    'BalanceEvent',
    function balanceEventListener() {
        const payload = normalizeBalanceEvent(
            getArguments(arguments),
        )

        console.log(
            '[NotifyHub] BalanceEvent:',
            payload,
        )

        emit(
            NOTIFICATION_BUS_EVENTS.BALANCE_EVENT,
            payload,
        )

        emitRefresh(
            'BalanceEvent',
            payload,
        )
    },
)

activityHub.on(
    'NewNotifications',
    function newNotificationsListener() {
        const payload = normalizeArguments(
            getArguments(arguments),
        )

        console.log(
            '[NotifyHub] NewNotifications:',
            payload,
        )

        emit(
            NOTIFICATION_BUS_EVENTS.NEW_NOTIFICATIONS,
            payload,
        )

        emitRefresh(
            'NewNotifications',
            payload,
        )
    },
)

activityHub.on(
    'CreateWithdrawForAdmin',
    function withdrawApprovedToBoosterListener() {
        const payload = normalizeArguments(
            getArguments(arguments),
        )

        console.log(
            '[NotifyHub] CreateWithdrawForAdmin:',
            payload,
        )

        emit(
            WITHDRAW_APPROVED_EVENT,
            payload,
        )

        emitRefresh(
            'CreateWithdrawForAdmin',
            payload,
        )
    },
)

activityHub.onreconnecting(error => {
    console.warn(
        '[NotifyHub] Reconnecting:',
        error,
    )

    emit(
        NOTIFICATION_BUS_EVENTS.RECONNECTING,
        error,
    )
})

activityHub.onreconnected(connectionId => {
    console.log(
        '[NotifyHub] Reconnected:',
        connectionId,
    )

    emit(
        NOTIFICATION_BUS_EVENTS.CONNECTED,
        {
            connectionId,
            state: activityHub.state,
            isReconnected: true,
        },
    )

    emitRefresh('Reconnected')
})

activityHub.onclose(error => {
    console.warn(
        '[NotifyHub] Closed:',
        error,
    )

    emit(
        NOTIFICATION_BUS_EVENTS.DISCONNECTED,
        error,
    )

    if (shouldBeConnected) {
        scheduleRestart()
    }
})

export async function startNotificationHub() {
    shouldBeConnected = true

    if (
        activityHub.state === HubConnectionState.Connected
        || activityHub.state === HubConnectionState.Connecting
        || activityHub.state === HubConnectionState.Reconnecting
    ) {
        return activityHub
    }

    if (startPromise) {
        return startPromise
    }

    if (!getAccessToken()) {
        console.warn(
            '[NotifyHub] Token is empty.',
        )

        emit(
            NOTIFICATION_BUS_EVENTS.DISCONNECTED,
            {
                reason: 'TokenIsEmpty',
            },
        )

        return null
    }

    startPromise = activityHub
        .start()
        .then(() => {
            console.log(
                '[NotifyHub] Connected:',
                {
                    url: hubUrl,
                    state: activityHub.state,
                },
            )

            emit(
                NOTIFICATION_BUS_EVENTS.CONNECTED,
                {
                    connectionId: activityHub.connectionId,
                    state: activityHub.state,
                    isReconnected: false,
                },
            )

            return activityHub
        })
        .catch(error => {
            console.error(
                '[NotifyHub] Connection failed:',
                error,
            )

            emit(
                NOTIFICATION_BUS_EVENTS.DISCONNECTED,
                error,
            )

            scheduleRestart()

            return null
        })
        .finally(() => {
            startPromise = null
        })

    return startPromise
}

export async function stopNotificationHub() {
    shouldBeConnected = false
    lastUserData = null

    if (restartTimer) {
        clearTimeout(restartTimer)
        restartTimer = null
    }

    if (
        activityHub.state
        === HubConnectionState.Disconnected
    ) {
        return
    }

    try {
        await activityHub.stop()
    } catch (error) {
        console.error(
            '[NotifyHub] Stop failed:',
            error,
        )
    }
}

export function getNotificationHub() {
    return activityHub
}

export function getLastUserData() {
    return lastUserData
}

export default activityHub
